/** @jsxImportSource @emotion/react */
import { AdjustmentCode, RemarkCodeList } from 'components/lab/billingEraReports/AdjustmentCodes'
import {
  DENIAL_ACTION_LABELS,
  denialResolutionLabel,
  DENIAL_STATUS_LABELS,
  OPEN_DENIAL_STATUSES,
} from 'components/lab/billingDenial/consts'
import { DenialActionKind, useDenialActions } from 'components/lab/billingDenial/DenialActions'
import { ResolveDenialModal } from 'components/lab/billingDenial/ResolveDenialModal'
import { formatYyyyMmDd } from 'components/lab/billingPayment/ServiceLineRow'
import { UserPermission } from 'components/lab/user/permissions'
import { formatCalendarDate, formatEasternDateTime } from 'helpers/dates'
import { useLabPermission } from 'hooks/useLabPermission'
import React from 'react'
import { Button } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetChargeDenialsQuery } from 'store/services/billingDenials'
import { BillingDenialDto, BillingDenialResolveAction } from 'store/types'

type Props = {
  chargeId?: string
  patientId?: string
  // The charge page's "mocked" submission switch, passed on to a resubmit.
  mocked?: boolean
  // Unsaved charge edits would not go out with a resubmitted claim.
  resubmitDisabledReason?: string
}

const money = (value: number) => `$${value.toFixed(2)}`

const OWNER_COLOR = '#8a5a00'

// Outlined in the owner strip's amber.
const ownerButtonCss = {
  '&, &:disabled': { background: 'transparent !important', borderColor: '#e0a64a !important', color: `${OWNER_COLOR} !important` },
  '&:not(:disabled):hover, &:not(:disabled):active': { background: '#fff4e5 !important', borderColor: '#c98a2a !important' },
  '&:focus': { boxShadow: 'none !important' },
}

const DenialBlock = ({
  denial,
  canAct,
  currentUserId,
  resubmitDisabledReason,
  onResolve,
  onAction,
}: {
  denial: BillingDenialDto
  canAct: boolean
  currentUserId: number | null
  resubmitDisabledReason?: string
  onResolve: (action: BillingDenialResolveAction) => void
  onAction: (kind: DenialActionKind) => void
}) => {
  const [showHistory, setShowHistory] = React.useState(false)
  const isOpen = OPEN_DENIAL_STATUSES.includes(denial.status)

  return (
    <div
      data-testid="charge-denial"
      css={{
        background: isOpen ? '#fff4e5' : '#f1f3f5',
        border: `1px solid ${isOpen ? '#f5c98b' : '#dee2e6'}`,
        borderRadius: 5,
        padding: '10px 20px',
        marginBottom: 12,
      }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div css={{ fontWeight: 600 }}>
          <i className={`fas fa-ban mr-2 ${isOpen ? 'text-warning' : 'text-secondary'}`} />
          Denied by {denial.payerName || 'payer'} on {formatCalendarDate(denial.denialDate)}
          <span css={{ fontWeight: 400, marginLeft: 8 }}>
            {DENIAL_STATUS_LABELS[denial.status]}
            {denial.resolution && ` · ${denialResolutionLabel(denial.resolution, denial.rebilledPayerName)}`}
            {' · '}
            {denial.source === 'ERA' ? 'From ERA' : 'Entered by hand'}
            {denial.payerClaimNumber && ` · Payer claim # ${denial.payerClaimNumber}`}
            {denial.traceNumber && ` · Check/EFT ${denial.traceNumber}`}
          </span>
        </div>
        {!isOpen && canAct && (
          <Button size="sm" variant="outline-secondary" onClick={() => onAction('REOPEN')}>
            Reopen
          </Button>
        )}
        {isOpen && canAct && (
          <div className="d-flex flex-wrap" css={{ gap: 8 }}>
            {/* An In Process denial is already being worked; appealing it changes nothing. */}
            {denial.status === 'PENDING' && (
              <Button size="sm" variant="outline-primary" onClick={() => onAction('APPEAL')}>
                Appeal
              </Button>
            )}
            <Button
              size="sm"
              variant="outline-primary"
              title={resubmitDisabledReason}
              disabled={!!resubmitDisabledReason}
              onClick={() => onAction('RESUBMIT')}>
              Resubmit Claim
            </Button>
            {denial.status === 'PENDING' && (
              <Button size="sm" variant="outline-primary" onClick={() => onAction('IN_PROCESS')}>
                Move to In Process
              </Button>
            )}
            <Button size="sm" variant="outline-primary" onClick={() => onResolve('WRITE_OFF')}>
              Write Off
            </Button>
            <Button size="sm" variant="outline-primary" onClick={() => onResolve('PATIENT')}>
              Push to Patient
            </Button>
          </div>
        )}
      </div>
      {isOpen && (
        <div
          className="d-flex align-items-center mt-2"
          css={{
            background: '#fff',
            border: '1px solid #f5c98b',
            borderRadius: 4,
            color: OWNER_COLOR,
            fontSize: 13,
            gap: 8,
            padding: '6px 10px',
          }}>
          <span className="mr-auto">
            <i className={`fas ${denial.ownerUserId ? 'fa-user-lock' : 'fa-user-unlock'} mr-2`} />
            {denial.ownerUserId ? (
              <>
                Assigned to <strong>{denial.ownerName || `user ${denial.ownerUserId}`}</strong>
              </>
            ) : (
              'Unassigned'
            )}
          </span>
          {canAct && (
            <>
              {!!currentUserId && (
                <Button
                  size="sm"
                  css={ownerButtonCss}
                  disabled={Number(denial.ownerUserId) === currentUserId}
                  onClick={() => onAction('ASSIGN_TO_ME')}>
                  Assign to me
                </Button>
              )}
              <Button size="sm" css={ownerButtonCss} onClick={() => onAction('ASSIGN')}>
                {denial.ownerUserId ? 'Reassign' : 'Assign'}
              </Button>
              <Button size="sm" css={ownerButtonCss} disabled={!denial.ownerUserId} onClick={() => onAction('UNASSIGN')}>
                Unassign
              </Button>
            </>
          )}
        </div>
      )}
      <table className="table table-sm mb-1 mt-2" css={{ background: '#fff', fontSize: 13 }}>
        <thead>
          <tr css={{ '& > th': { fontWeight: 500 } }}>
            <th>CPT</th>
            <th>DOS</th>
            <th>Charged</th>
            <th>Allowed</th>
            <th>Paid</th>
            <th>Patient resp.</th>
            <th>Codes</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {denial.lines.map((line) => (
            <tr key={line.id}>
              <td>{line.cptCode}</td>
              <td>{formatYyyyMmDd(line.startDos)}</td>
              <td>{money(line.charged)}</td>
              <td>{money(line.allowed)}</td>
              <td>{money(line.paid)}</td>
              <td>{money(line.patientResponsibility)}</td>
              <td>
                {(line.adjustments ?? []).map((adjustment, index) => (
                  <span key={index} className="mr-1" title={adjustment.amount !== undefined ? money(Number(adjustment.amount)) : undefined}>
                    <AdjustmentCode group={adjustment.group} reason={adjustment.reason} />
                  </span>
                ))}
              </td>
              <td>
                <RemarkCodeList codes={line.remarkCodes} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        variant="link"
        size="sm"
        type="button"
        className="p-0"
        css={{ fontSize: 13, textDecoration: 'none' }}
        onClick={() => setShowHistory((x) => !x)}>
        <i className={`fas fa-chevron-${showHistory ? 'down' : 'right'} mr-1`} css={{ fontSize: 10 }} />
        History ({denial.history.length})
      </Button>
      {showHistory && (
        // About ten rows, then it scrolls.
        <div className="mt-1" css={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="table table-sm mb-0" css={{ background: '#fff', fontSize: 12 }}>
            <thead>
              <tr css={{ '& > th': { background: '#fff', fontWeight: 500, position: 'sticky', top: 0 } }}>
                <th>Date</th>
                <th>Action</th>
                <th>Status</th>
                <th>User</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {[...denial.history].reverse().map((entry, index) => (
                <tr key={index}>
                  <td css={{ whiteSpace: 'nowrap' }}>{formatEasternDateTime(entry.createdAt)}</td>
                  <td css={{ fontWeight: 500 }}>{DENIAL_ACTION_LABELS[entry.action]}</td>
                  <td css={{ color: '#6c757d' }}>
                    {entry.fromStatus && entry.fromStatus !== entry.toStatus
                      ? `${DENIAL_STATUS_LABELS[entry.fromStatus]} → ${DENIAL_STATUS_LABELS[entry.toStatus]}`
                      : DENIAL_STATUS_LABELS[entry.toStatus]}
                  </td>
                  <td>{entry.userName || 'System'}</td>
                  <td css={{ minWidth: 180 }}>{entry.note || <span className="text-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * The payer denials recorded on this charge, open ones with the actions that
 * resolve them. Recording a denial moved no money; resolving it does.
 */
export const ChargeDenials = ({ chargeId, patientId, mocked, resubmitDisabledReason }: Props) => {
  const canAct = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const { data: denials } = useGetChargeDenialsQuery(
    chargeId && patientId ? { chargeId, patientId: Number(patientId) } : skipToken,
  )
  const [resolving, setResolving] = React.useState<{ denial: BillingDenialDto; action: BillingDenialResolveAction } | null>(null)
  const { run, modals, currentUserId } = useDenialActions({ mocked })

  if (!denials?.length) {
    return null
  }

  return (
    <div data-testid="charge-denials">
      {denials.map((denial) => (
        <DenialBlock
          key={denial.id}
          denial={denial}
          canAct={canAct}
          currentUserId={currentUserId}
          resubmitDisabledReason={resubmitDisabledReason}
          onResolve={(action) => setResolving({ denial, action })}
          onAction={(kind) => run(kind, [denial.id])}
        />
      ))}
      {resolving && (
        <ResolveDenialModal
          denial={resolving.denial}
          action={resolving.action}
          onClose={() => setResolving(null)}
        />
      )}
      {modals}
    </div>
  )
}
